import React, { useContext } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgTrash from '@icons/delete-trash.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import { CarrierDetailAdditionalInfoOutDTO } from '@api/ringil-api.v2.tsx';
import SvgInfo from '@icons/info.svg?react';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Calendar } from 'primereact/calendar';
import * as yup from 'yup';
import useForm from '@hooks/useForm/useForm.tsx';
import { ApiContext } from '@api/api.tsx';
import { formatCarrierAdditionInfo } from '@utils/utils.tsx';

interface ComponentProps {
  carrierId: string;
  carrierDetailAdditionalInfos: CarrierDetailAdditionalInfoOutDTO[];
  readOnly?: boolean;
  reloadData: () => void;
}

interface FormValues {
  infos: CarrierDetailAdditionalInfoOutDTO[];
}

const CarrierAdditionalInfoTag: React.FC<ComponentProps> = ({
                                                              carrierId,
                                                              carrierDetailAdditionalInfos,
                                                              readOnly = false,
                                                              reloadData,
                                                            }) => {
  const { tr } = useTranslationLgs();
  const {
    createAdditionalInfoForCarrier,
  } = useContext(ApiContext);

  const handleDelete = (index: number) => {
    const updated = form.values.infos.filter((_, i) => i !== index);
    form.setFieldValue('infos', updated);
  };

  const handleAddNew = () => {
    form.setFieldValue('infos', [...form.values.infos, { text: '', from: null, to: null }]);
  };

  const schema = yup.object().shape({
    infos: yup.array().of(
      yup.object().shape({
        text: yup.string().required(),
        from: yup.date().required().nullable(),
        to: yup.date().required().nullable(),
      }),
    ),
  });

  const formInitialValues: FormValues = {
    infos: carrierDetailAdditionalInfos ?? [],
  };

  const onContinueHandler = (data: FormValues) => {
    createAdditionalInfoForCarrier(
      carrierId,
      { infos: data.infos },
      () => {
        reloadData();
      },
    );
  };

  const form = useForm<FormValues>(
    schema,
    formInitialValues,
    (d) => onContinueHandler(d),
    false,
    false,
    () => {
    },
    'none',
  );

  return (
    <>
      <ButtonTag
        label={tr('CarrierDetailAbout.tagAdditionInfo', 'Additional info')}
        value={formatCarrierAdditionInfo(carrierDetailAdditionalInfos)}
        icon={<SvgInfo />}
        onSave={onSuccess => {
          form.validateAndSend();
          onSuccess();
        }}
        autoClose
        readonly={readOnly}
      >
        <div className="w-[500px]">
          {form.values.infos.map((info, index) => (
            <div key={index} className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 w-full items-end">
                <div className="flex-1">
                  <Field
                    label={tr('CarrierDetailAbout.textLabel', 'Text')}
                    required
                  >
                    <RingilInputText
                      value={form.values.infos?.[index]?.text ?? ''}
                      onChange={value => form.setFieldValue(`infos[${index}].text`, value)}
                      placeholder={tr('CarrierDetailAbout.textPlaceholder', 'Text')}
                      fullWidth
                    />
                  </Field>
                </div>
                <RingilButton
                  mode={'table'}
                  icon={<SvgTrash className="mx-auto" />}
                  onClick={() => handleDelete(index)}
                  className="w-[42px] h-[42px]"
                />
              </div>
              <div className="flex flex-row gap-2">
                <Field
                  label={tr('CarrierDetailAbout.fromLabel', 'From')}
                  required
                >
                 <Calendar
                    value={form.values.infos?.[index]?.from ? new Date(form.values.infos[index].from) : null}
                    onChange={e => form.setFieldValue(
                      `infos[${index}].from`,
                      e.value instanceof Date ? e.value.toISOString() : null
                    )}
                    placeholder={tr('CarrierDetailAbout.fromPlaceholder', 'From')}
                  />
                </Field>
                <Field
                  label={tr('CarrierDetailAbout.toLabel', 'To')}
                  required
                >
                 <Calendar
                    value={form.values.infos?.[index]?.to ? new Date(form.values.infos[index].to) : null}
                    onChange={e => form.setFieldValue(
                      `infos[${index}].to`,
                      e.value instanceof Date ? e.value.toISOString() : null
                    )}
                    placeholder={tr('CarrierDetailAbout.toPlaceholder', 'To')}
                  />
                </Field>
              </div>
              <div className="w-full h-px bg-content-tertiary"></div>
            </div>
          ))}
          <div className="mt-2">
            <RingilButton
              mode={'text-only'}
              label={tr('CarrierDetailAbout.addNew', 'Add new')}
              icon={<SvgPlus />}
              onClick={handleAddNew}
            />
          </div>
        </div>
      </ButtonTag>
    </>
  );
};

export default CarrierAdditionalInfoTag;
